import { useState, useMemo } from "react"
import { router } from "@inertiajs/react"
import { X, FolderOpen, Loader2 } from "lucide-react"
import { FolderPicker } from "@/components/capture/FolderPicker"
import { useBlockAutoReload } from "@/lib/standalone-recovery"

interface FrontMatterField {
  id: number
  name: string
  options: Array<{ id: number; label: string }>
}

interface RoutingRule {
  front_matter_field_id: number
  front_matter_option_id: number
  destination_folder: string
}

interface SaveNoteDialogProps {
  frontMatterFields: FrontMatterField[]
  defaultDestination: string
  rootFolder: string
  fileNameTemplate: string
  routingRules: RoutingRule[]
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

export function SaveNoteDialog({
  frontMatterFields,
  defaultDestination,
  rootFolder,
  fileNameTemplate,
  routingRules,
}: SaveNoteDialogProps) {
  const [input, setInput] = useState("")
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number>>({})
  const [destination, setDestination] = useState(defaultDestination)
  const [showFolderPicker, setShowFolderPicker] = useState(false)
  const [saving, setSaving] = useState(false)

  // Block the standalone recovery reload while the user has typed a title or a
  // save is in flight, so a backgrounded session doesn't lose the new note.
  useBlockAutoReload(input.trim().length > 0 || saving)

  // Compute the date prefix from the template (everything before {input})
  const { prefix, placeholder } = useMemo(() => {
    const now = new Date()
    const template = fileNameTemplate
      .replace("{YYYY}", String(now.getFullYear()))
      .replace("{MM}", String(now.getMonth() + 1).padStart(2, "0"))
      .replace("{DD}", String(now.getDate()).padStart(2, "0"))
      .replace("{HH}", String(now.getHours()).padStart(2, "0"))
      .replace("{mm}", String(now.getMinutes()).padStart(2, "0"))

    const inputIdx = template.indexOf("{input}")
    if (inputIdx === -1) {
      return { prefix: template, placeholder: "" }
    }
    return {
      prefix: template.slice(0, inputIdx),
      placeholder: "describe this file...",
    }
  }, [fileNameTemplate])

  // Compute full file name
  const fileName = useMemo(() => {
    const base = prefix + (input.trim() || "")
    return base.replace(/-+$/, "")
  }, [prefix, input])

  function handleOptionSelect(fieldId: number, optionId: number) {
    const newSelected = { ...selectedOptions }

    if (newSelected[fieldId] === optionId) {
      delete newSelected[fieldId]
    } else {
      newSelected[fieldId] = optionId
    }

    setSelectedOptions(newSelected)

    // Check routing rules for auto-destination
    const matchingRule = routingRules.find(
      (r) => r.front_matter_field_id === fieldId && r.front_matter_option_id === optionId
    )
    if (matchingRule && newSelected[fieldId] === optionId) {
      setDestination(matchingRule.destination_folder)
    }
  }

  async function handleSave() {
    setSaving(true)

    try {
      // Build front matter from selected options
      const frontMatter: Record<string, string> = {}
      for (const field of frontMatterFields) {
        const selectedOptId = selectedOptions[field.id]
        if (selectedOptId) {
          const option = field.options.find((o) => o.id === selectedOptId)
          if (option) {
            frontMatter[field.name] = option.label
          }
        }
      }

      const title = input.trim()
      const initialBody = title ? `# ${title}\n` : ""

      const res = await fetch("/api/files", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCSRFToken(),
        },
        body: JSON.stringify({
          file_name: fileName,
          destination_path: destination,
          front_matter: frontMatter,
          body: initialBody,
        }),
      })

      const data = await res.json()
      // Navigate to the editor with the new file path. `?new=1` tells the
      // editor this is a freshly created note so it can place the cursor on
      // an empty line below the auto-inserted heading.
      const path = (data.path as string).replace(/^\/+/, "")
      const encodedPath = path.split("/").map(segment => encodeURIComponent(segment)).join("/")
      const suffix = title ? "?new=1" : ""
      router.visit(`/files/${encodedPath}${suffix}`)
    } catch {
      setSaving(false)
    }
  }

  function handleExit() {
    router.visit("/")
  }

  return (
    <div className="fixed inset-0 z-50 bg-background overflow-y-auto">
      {/* Top bar */}
      <div className="sticky top-0 z-30 flex items-center justify-between p-4 bg-background/80 backdrop-blur-sm border-b">
        <button
          onClick={handleExit}
          className="flex items-center justify-center w-9 h-9 rounded-full bg-muted text-muted-foreground hover:bg-accent transition-colors"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <span className="text-sm font-medium text-foreground">
          Start your note
        </span>
        <div className="w-9" />
      </div>

      <div className="max-w-lg lg:max-w-xl mx-auto px-5 py-8 space-y-6">
        {/* File name + destination container */}
        <div className="rounded-lg border border-border/60 bg-card p-4 space-y-4">
          <div>
            <div className="flex items-center rounded-lg border border-border/60 overflow-hidden">
              <span
                className="text-sm text-muted-foreground pl-4 py-3 shrink-0 select-none bg-muted/80 border-r border-border/60"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {prefix}
              </span>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleSave()
                  }
                }}
                placeholder={placeholder}
                className="flex-1 bg-white dark:bg-background text-sm text-foreground py-3 pr-4 pl-3 outline-none border-0 rounded-none shadow-none focus:ring-0 focus:outline-none placeholder:text-muted-foreground/50"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
                autoFocus
                data-testid="file-name-input"
              />
            </div>
            <p
              className="mt-2 text-xs text-muted-foreground"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
              data-testid="file-name-preview"
            >
              {fileName}.md
            </p>
          </div>

          <div className="border-t border-border/60 pt-3">
            <div className="flex items-center justify-between p-3 rounded-lg border border-border/60 bg-muted/50">
              <div className="flex items-center gap-2.5 min-w-0">
                <FolderOpen
                  className="w-4 h-4 text-muted-foreground shrink-0"
                  strokeWidth={1.5}
                />
                <span
                  className="text-sm text-foreground truncate"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  {destination}
                </span>
              </div>
              <button
                onClick={() => setShowFolderPicker(true)}
                className="text-xs font-medium text-primary hover:text-primary/80 transition-colors shrink-0 ml-3"
              >
                Change
              </button>
            </div>
          </div>
        </div>

        {/* Front matter tagging container */}
        {frontMatterFields.length > 0 && (
          <div className="rounded-lg border border-border/60 bg-card p-4 space-y-5">
            {frontMatterFields.map((field) => (
              <div key={field.id}>
                <p className="text-xs font-medium text-muted-foreground mb-2 capitalize">
                  {field.name}
                </p>
                <div className="flex flex-wrap gap-2">
                  {field.options.map((option) => {
                    const isSelected = selectedOptions[field.id] === option.id
                    return (
                      <button
                        key={option.id}
                        onClick={() => handleOptionSelect(field.id, option.id)}
                        className={`px-3.5 py-2 rounded-md text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
                          isSelected
                            ? "bg-secondary text-secondary-foreground shadow-xs shadow-secondary/20"
                            : "bg-muted/50 border text-muted-foreground hover:border-border hover:text-foreground"
                        }`}
                        data-testid={`tag-${field.name}-${option.label}`}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-3.5 rounded-lg bg-primary hover:bg-primary/90 active:bg-primary/80 text-primary-foreground text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-xs shadow-primary/20"
          data-testid="save-button"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
              Creating...
            </>
          ) : (
            "Create"
          )}
        </button>
      </div>

      <FolderPicker
        open={showFolderPicker}
        onClose={() => setShowFolderPicker(false)}
        onSelect={(path) => {
          setDestination(path)
          setShowFolderPicker(false)
        }}
        currentPath={destination}
        rootPath={rootFolder}
      />
    </div>
  )
}
